import { useForm } from 'react-hook-form';
import styles from './TestsPage.module.css';
import Badge from 'react-bootstrap/Badge';
import Line from '../../../Components/Charts/Line/Line';
import { ProgressBar } from 'react-bootstrap';
import { Link, useParams } from 'react-router';
import { useUser } from '../../../Context/useUser';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import API from '../../../Utils/api';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { useEffect } from 'react';
import Utils from '../../../Utils';
import Table from '../../../Components/Table/Table';

export default function T50v2TestPage () {
    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [t50Tests, setT50Tests] = useState([]);

    const [selectedRows, setSelectedRows] = useState(t50Tests);

    const handleRowClick = row => {
        setSelectedRows(prev => {
            const alreadySelected = prev.some(r => r._id === row._id);
            if (alreadySelected) {
                return prev.filter(r => r._id !== row._id);
            } else {
                return [...prev, row];
            }
        });
    };

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;

        try {
            const { data } = await API.getTest50TestsByUserId(currentUser._id);
            if (data) {
                setT50Tests(data);
                setSelectedRows(data[0] ? [data[0]] : []);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        }
    };

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            volume50: 50,
            volume100: 100,
            volume200: 200,
            volume400: 400,
            time50: '00:00,000',
            time100: '00:00,000',
            time200: '00:00,000',
            time400: '00:00,000',
            poolSize: '',
            testDate: ''
        }
    });

    const currentInput = watch();

    const onSubmit = async () => {
        try {
            const testDateValue = currentInput.testDate
                ? new Date(currentInput.testDate).getTime()
                : undefined;

            const { data } = await API.createTest50Test(currentUser._id, {
                time50: time50Meters,
                time100: time100Meters,
                time200: time200Meters,
                time400: time400Meters,
                poolSize: currentInput.poolSize ? Number(currentInput.poolSize) : null,
                testDate: testDateValue
            });

            currentUser.currentCSS = Number(data.css);
            currentUser.currentMSS = Number(data.mss);
            toast.success('Test created successfully!', {
                position: 'bottom-center'
            });
            await fetchAndSetData(currentUser);
        } catch (error) {
            toast.error(`Failed to create test - please contact support`, {
                position: 'bottom-center'
            });
        }
        reset();
    };

    function parseDuration (durationStr) {
        // Expects "mm:ss,ms"
        const match = durationStr.match(/^([0-5]?\d):([0-5]\d),(\d{1,3})$/);
        if (!match) return 0;
        const min = Number(match[1]);
        const sec = Number(match[2]);
        const ms = Number(match[3].padEnd(3, '0')); // pad to milliseconds if needed
        return min * 60 + sec + ms / 1000;
    }

    function calculateVelocity (distance, durationStr) {
        // durationStr is in "mm:ss" format
        if (!distance || !durationStr) return 0;
        const totalSeconds = parseDuration(durationStr);
        if (totalSeconds === 0) return 0;
        return (distance / totalSeconds).toFixed(2);
    }

    const time50Meters = currentInput.time50 ? parseDuration(currentInput.time50) : 0;
    const time100Meters = currentInput.time100 ? parseDuration(currentInput.time100) : 0;
    const time200Meters = currentInput.time200 ? parseDuration(currentInput.time200) : 0;
    const time400Meters = currentInput.time400 ? parseDuration(currentInput.time400) : 0;

    const velocity50 = currentInput.time50 ? 50 / time50Meters : 0;
    const velocity100 = currentInput.time100 ? 100 / time100Meters : 0;
    const velocity200 = currentInput.time200 ? 200 / time200Meters : 0;
    const velocity400 = currentInput.time400 ? 400 / time400Meters : 0;

    const calculateCSS = (time50Meters, time100Meters, time200Meters, time400Meters) => {
        if (time50Meters === 0 || time200Meters === 0 || time400Meters === 0) {
            return 0; // Avoid division by zero
        }

        // Calculate mean (average) time and distance
        const meanTime = (time50Meters + time100Meters + time200Meters + time400Meters) / 4;
        const meanDistance = (50 + 100 + 200 + 400) / 4;

        // Calculate differences between each time value and the mean time
        const diffTime50 = time50Meters - meanTime;
        const diffTime100 = time100Meters - meanTime;
        const diffTime200 = time200Meters - meanTime;
        const diffTime400 = time400Meters - meanTime;

        // Calculate differences between each distance value and the mean distance
        const diffDist50 = 50 - meanDistance;
        const diffDist100 = 100 - meanDistance;
        const diffDist200 = 200 - meanDistance;
        const diffDist400 = 400 - meanDistance;

        // Calculate sum of products of differences
        const sumProducts =
            diffTime50 * diffDist50 +
            diffTime100 * diffDist100 +
            diffTime200 * diffDist200 +
            diffTime400 * diffDist400;

        // Calculate sum of squared differences
        const sumSquaredTimeDiffs =
            Math.pow(diffTime50, 2) +
            Math.pow(diffTime100, 2) +
            Math.pow(diffTime200, 2) +
            Math.pow(diffTime400, 2);

        // Calculate CSS
        return sumProducts / sumSquaredTimeDiffs;
    };

    const calculateMSS = time50Meters => (time50Meters ? 50 / time50Meters : 0);

    const columns = [
        {
            Header: 'Date',
            accessor: 'testDate',
            Cell: ({ row }) => {
                const date = new Date(row.original?.testDate);
                return (
                    <div>
                        {date.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        })}
                    </div>
                );
            }
        },
        {
            id: 'time50m',
            Header: () => <div style={{ textAlign: 'center' }}>Time 50m (sec)</div>,
            accessor: 'time50m',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.time50}</div>
        },
        {
            id: 'time100m',
            Header: () => <div style={{ textAlign: 'center' }}>Time 100m (sec)</div>,
            accessor: 'time100m',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.time100}</div>
        },
        {
            id: 'time200m',
            Header: () => <div style={{ textAlign: 'center' }}>Time 200m (sec)</div>,
            accessor: 'time200m',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.time200}</div>
        },
        {
            id: 'time400m',
            Header: () => <div style={{ textAlign: 'center' }}>Time 400m (sec)</div>,
            accessor: 'time400m',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.time400}</div>
        },
        {
            id: 'poolSize',
            Header: () => <div style={{ textAlign: 'center' }}>Pool Size</div>,
            accessor: 'pool',
            Cell: ({ row }) => (
                <div style={{ textAlign: 'center' }}>{row.original.poolSize ? row.original.poolSize + 'm' : 'N/A'}</div>
            )
        },
        {
            id: 'css',
            Header: () => <div style={{ textAlign: 'center' }}>CSS</div>,
            accessor: 'css',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.css?.toFixed(2)}</div>
        },
        {
            id: 'mss',
            Header: () => <div style={{ textAlign: 'center' }}>MSS</div>,
            accessor: 'mss',
            Cell: ({ row }) => <div style={{ textAlign: 'center' }}>{row.original.mss?.toFixed(2)}</div>
        }
    ];

    const currentTestData = {
        time50: time50Meters,
        time100: time100Meters,
        time200: time200Meters,
        time400: time400Meters,
        velocity50: velocity50,
        velocity100: velocity100,
        velocity200: velocity200,
        velocity400: velocity400
    };

    const combinedChartData = [
        ...selectedRows,
        // Only include current input if both times are valid (to prevent plotting 0,0)
        ...(time50Meters > 0 && time100Meters > 0 && time200Meters > 0 && time400Meters > 0
            ? [currentTestData]
            : [])
    ];

    const datasetsDistanceTime = combinedChartData.map(row => {
        const { red, green, blue } = Utils.randomColorGenerator();
        return {
            label: row.testDate
                ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                : 'Current Test',
            data: [
                { x: row.time50, y: 50 },
                { x: row.time100, y: 100 },
                { x: row.time200, y: 200 },
                { x: row.time400, y: 400 }
            ],
            borderColor: `rgb(${red}, ${green}, ${blue})`,
            backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
            tension: 0.1,
            fill: false,
            showLine: true
        };
    });

    const datasetsDistanceVelocity = combinedChartData
        .filter(row => row.time50 > 0 && row.time100 > 0 && row.time200 > 0 && row.time400 > 0)
        .map(row => {
            const { red, green, blue } = Utils.randomColorGenerator();
            return {
                label: row.testDate
                    ? `Test on ${new Date(row.testDate).toLocaleDateString('en-GB')}`
                    : 'Current Test',
                data: [
                    { x: row.velocity50, y: 50 },
                    { x: row.velocity100, y: 100 },
                    { x: row.velocity200, y: 200 },
                    { x: row.velocity400, y: 400 }
                ],
                borderColor: `rgb(${red}, ${green}, ${blue})`,
                backgroundColor: `rgba(${red}, ${green}, ${blue}, 0.5)`,
                tension: 0.1,
                fill: false,
                showLine: true
            };
        });

    return (
        <div className={styles.testPageWrap}>
            <div className={styles.topPageProgressBarWrap + ' island'}>
                <span>
                    <b>Test Difficulty:</b>
                </span>
                <div className={styles.progressBarWrap}>
                    <ProgressBar>
                        <ProgressBar animated striped variant='danger' now={90} />
                    </ProgressBar>
                </div>
            </div>
            <div className={styles.topPageWrap}>
                <div className={styles.currentTestAndSummary}>
                    <div className={styles.dataCollection + ' island'}>
                        <h2 className={styles.dataCollectionMainTitle}>Test Data</h2>
                        <h3 className={styles.dataCollectionSubTitle}>
                            Please enter your test data results.
                        </h3>
                        <form className={styles.dataCollectionForm}>
                            <div className={styles.dataCollectionFormRow}>
                                <div className='form-group'>
                                    <label htmlFor='volume50'>Meters (m)</label>
                                    <input
                                        disabled={true}
                                        {...register('volume50', { required: true })}
                                        className='form-control'
                                    />
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='time50'>Duration (min)</label>
                                    <input
                                        type='text'
                                        placeholder='mm:ss,ms'
                                        {...register('time50', {
                                            required: 'Time is required',
                                            pattern: {
                                                value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                                message: "'mm:ss,ms' format is required"
                                            }
                                        })}
                                        className='form-control'
                                    />
                                    {errors.time50 && (
                                        <span className={styles.formInputError}>
                                            {errors.time50.message}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='velocity50'>Velocity (m/sec)</label>
                                    <input
                                        disabled={true}
                                        type='number'
                                        value={calculateVelocity(
                                            currentInput.volume50,
                                            currentInput.time50
                                        )}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        {...register('volume100', { required: true })}
                                        className='form-control'
                                    />
                                </div>
                                <div className='form-group'>
                                    <input
                                        type='text'
                                        placeholder='mm:ss,ms'
                                        {...register('time100', {
                                            required: 'Time is required',
                                            pattern: {
                                                value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                                message: "'mm:ss,ms' format is required"
                                            }
                                        })}
                                        className='form-control'
                                    />
                                    {errors.time100 && (
                                        <span className={styles.formInputError}>
                                            {errors.time100.message}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        type='number'
                                        value={calculateVelocity(
                                            currentInput.volume100,
                                            currentInput.time100
                                        )}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        {...register('volume200', { required: true })}
                                        className='form-control'
                                    />
                                </div>
                                <div className='form-group'>
                                    <input
                                        type='text'
                                        placeholder='mm:ss,ms'
                                        {...register('time200', {
                                            required: 'Time is required',
                                            pattern: {
                                                value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                                message: "'mm:ss,ms' format is required"
                                            }
                                        })}
                                        className='form-control'
                                    />
                                    {errors.time200 && (
                                        <span className={styles.formInputError}>
                                            {errors.time200.message}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        type='number'
                                        value={calculateVelocity(
                                            currentInput.volume200,
                                            currentInput.time200
                                        )}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        {...register('volume400', { required: true })}
                                        className='form-control'
                                    />
                                </div>
                                <div className='form-group'>
                                    <input
                                        type='text'
                                        placeholder='mm:ss,ms'
                                        {...register('time400', {
                                            required: 'Time is required',
                                            pattern: {
                                                value: /^([0-5]?\d):([0-5]\d),(\d{1,3})$/,
                                                message: "'mm:ss,ms' format is required"
                                            }
                                        })}
                                        className='form-control'
                                    />
                                    {errors.time400 && (
                                        <span className={styles.formInputError}>
                                            {errors.time400.message}
                                        </span>
                                    )}
                                </div>
                                <div className='form-group'>
                                    <input
                                        disabled={true}
                                        type='number'
                                        value={calculateVelocity(
                                            currentInput.volume400,
                                            currentInput.time400
                                        )}
                                        className='form-control'
                                    />
                                </div>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <label htmlFor='poolSize'>
                                    <b>Pool Size (m) </b>[For reference only]
                                </label>
                                <select className='form-control' {...register('poolSize')}>
                                    <option value={''}>-</option>
                                    <option value={25}>25m</option>
                                    <option value={50}>50m</option>
                                </select>
                            </div>
                            <div className={styles.dataCollectionFormRow}>
                                <label htmlFor='testDate'>
                                    <b>Test Date & Time</b> (Defaults to now)
                                </label>
                                <input
                                    type='datetime-local'
                                    {...register('testDate')}
                                    className='form-control'
                                />
                            </div>
                            <button
                                className={`${styles.dataCollectionFormButton} btn btn-primary`}
                                onClick={handleSubmit(onSubmit)}
                            >
                                Submit
                            </button>
                        </form>
                    </div>
                    <div className={styles.dataSummary + ' island'}>
                        <h2 className={styles.summaryTitle}>Summary</h2>
                        <div className={styles.summaryGroup}>
                            <div className={styles.summaryItems}>
                                <div className={styles.badgeGroup}>
                                    <span className={styles.currentTestHeader}>
                                        Current Test Results
                                    </span>
                                    <span className={styles.currentTestHeader}>
                                        Latest CSS & MSS for{' '}
                                        <b>{currentUser ? currentUser.firstName : ''}</b>
                                    </span>
                                </div>
                                <Badge pill bg='primary' className={styles.customCSSBadge}>
                                    Critical Swim Speed (CSS)
                                </Badge>
                                <div className={styles.badgeGroup}>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {calculateCSS(
                                            time50Meters,
                                            time100Meters,
                                            time200Meters,
                                            time400Meters
                                        ).toFixed(3)}
                                    </Badge>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {currentUser?.currentCSS?.toFixed(3) || 0}
                                    </Badge>
                                </div>
                                <Badge pill bg='primary' className={styles.customMSSBadge}>
                                    Maximum Swim Speed (MSS)
                                </Badge>
                                <div className={styles.badgeGroup}>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {calculateMSS(time50Meters).toFixed(3) || 0}
                                    </Badge>
                                    <Badge pill bg='secondary' className={styles.customValueBadge}>
                                        {currentUser?.currentMSS?.toFixed(3) || 0}
                                    </Badge>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.previousTestsWrap + ' island'}>
                    <h2 className={styles.previousTestsTitle}>Previous Tests</h2>
                    <Table
                        onRowClick={handleRowClick}
                        className={styles.previousTestsTable}
                        columns={columns}
                        data={t50Tests}
                        rowsPerPage={9}
                        updatePageSize={false}
                        pageSkipping={false}
                        selected={selectedRows}
                        showSelectionIndicator={true}
                        tableLayout='auto'
                    />
                </div>
                <div className={styles.dataInformation + ' island'}>
                    <h2 className={styles.informationTitle}>Information</h2>
                    <div className={styles.informationItems}>
                        <p>
                            This test is intended for <b>advanced</b> swimmers. It takes{' '}
                            <b>three</b> days to complete this test.
                            <br></br>
                            On the first day you will complete the <b>50</b> and <b>400</b> meter
                            tests with at least half an hour between them.
                            <br></br>
                            On the second day you will complete the <b>100-meter</b> test.
                            <br></br>
                            On the third day you will complete the <b>200-meter</b> test.
                            <br></br>
                            The four trials will start in the pool without a start.
                        </p>
                    </div>
                </div>
            </div>
            <div className={styles.bottomPageWrap}>
                <div className={styles.bottomPageChart + ' island'}>
                    <Line
                        className={styles.chart}
                        title={'Distance - Time'}
                        titleY={'Distance (m)'}
                        titleX={'Time (sec)'}
                        datasets={datasetsDistanceTime}
                        xAxisType='linear'
                    />
                </div>
                <div className={styles.bottomPageChart + ' island'}>
                    <Line
                        title={'Distance - Velocity'}
                        titleY={'Distance (m)'}
                        titleX={'Velocity (m/sec)'}
                        datasets={datasetsDistanceVelocity}
                        xAxisType='linear'
                    />
                </div>
            </div>
            <div className={styles.footerPageWrap + ' island'}>
                <span className={styles.footerPageWrapNote}>
                    For your Training Zones Limits check your&nbsp;
                </span>
                <span className={styles.footerPageWrapNoteLink}>
                    <Link to='../../dashboard' style={{ color: 'inherit', textDecoration: 'none' }}>
                        Dashboard.
                    </Link>
                </span>
            </div>
        </div>
    );
}
